var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { css, html, LitElement } from "lit";
import { property, state } from "lit/decorators.js";
import { FormControlMixin } from "../../../a2k-form-control/index.js";
import "../../../a2k-button/lib/src/a2k-button.js";
import "../../../a2k-icons/lib/src/a2k-icon.js";
export class A2kSelect extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        this.label = "";
        this.selectedItem = null;
        this.name = "";
        this.expanded = false;
        this.placeholder = "Choose an option";
        this._options = [];
        this._openListbox = () => {
            if (this.expanded)
                return;
            this.expanded = true;
            document.addEventListener("click", this._hideOnClickOutside);
        };
        this._closeListbox = () => {
            if (!this.expanded)
                return;
            this.expanded = false;
            document.removeEventListener("click", this._hideOnClickOutside);
        };
        this._hideOnClickOutside = (event) => {
            if (!event.composedPath().includes(this)) {
                this._closeListbox();
            }
        };
    }
    handleKeyDown(e) {
        if (e.key === "Escape" && this.expanded) {
            this._closeListbox();
        }
        else if (e.key === "Escape" && !this.expanded) {
            this.handleSetValue(null);
        }
    }
    handleSelectKeyDown(e) {
        if (e.key === "ArrowDown" || e.key === "Enter" || e.code === "Space") {
            if (!this.expanded) {
                this._openListbox();
            }
            else {
                this.moveFocusToOption(0);
            }
        }
    }
    moveFocusToOption(index) {
        const options = this._options;
        const option = options[index];
        option.focus();
    }
    handleSelectClick() {
        this.expanded ? this._closeListbox() : this._openListbox();
    }
    handleSetValue(item) {
        // create new change event and dispatch it
        const newItem = item ? item : null;
        this.selectedItem = newItem;
        this.setValue(newItem?.value || "");
        const customEvent = new Event("change", {
            composed: true,
            bubbles: true,
        });
        this.dispatchEvent(customEvent);
    }
    handleOptionSelect(e) {
        const target = e.target;
        if (target.tagName !== "OPTION")
            return;
        const item = {
            value: target.value,
            label: target.innerText,
        };
        this.handleSetValue(item);
        this._closeListbox();
    }
    handleOptionKeyDown(e) {
        if (e.key === "Enter" || e.code === "Space") {
            this.handleOptionSelect(e);
        }
        if (e.key === "ArrowDown") {
            const target = e.target;
            if (target.nextElementSibling) {
                const nextElement = target.nextElementSibling;
                nextElement.focus();
            }
            else {
                this.moveFocusToOption(0);
            }
        }
        if (e.key === "ArrowUp") {
            const target = e.target;
            if (target.previousElementSibling) {
                const prevElement = target.previousElementSibling;
                prevElement.focus();
            }
            else {
                const options = this._options;
                const len = options.length;
                this.moveFocusToOption(len - 1);
            }
        }
    }
    toggleButton() {
        return html `<a2k-button tabindex="-1" aria-label="Open Select" size="small">
      <a2k-icon icon="chevron-icon"></a2k-icon>
    </a2k-button> `;
    }
    handleSlotchange(e) {
        const target = e.target;
        const childNodes = target.assignedElements();
        childNodes.forEach((node) => {
            node.setAttribute("tabindex", "0");
        });
        this._options = childNodes;
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.label) {
            this.label = `${this.label}:`;
        }
        this.addEventListener("keydown", this.handleKeyDown);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("keydown", this.handleKeyDown);
    }
    render() {
        return html `
      <div id="select-wrapper">
        <label id="label">${this.label}</label>
        <div id="select">
          <div
            aria-controls="listbox"
            aria-expanded="${this.expanded}"
            aria-haspopup="listbox"
            aria-labelledby="label"
            class="combo-input"
            role="combobox"
            @keydown=${this.handleSelectKeyDown}
            @click=${this.handleSelectClick}
            tabindex="0"
            name=${this.name}
            value=${this.selectedItem?.value}
          >
            <p>
              ${this.selectedItem ? this.selectedItem.label : this.placeholder}
            </p>
            <slot name="icon"> ${this.toggleButton()} </slot>
          </div>

          ${this.expanded
            ? html `
                <div
                  class="listbox"
                  role="listbox"
                  id="listbox"
                  aria-labelledby="label"
                  tabindex="-1"
                  @click=${this.handleOptionSelect}
                  @keydown=${this.handleOptionKeyDown}
                >
                  <slot @slotchange=${this.handleSlotchange}></slot>
                </div>
              `
            : null}
        </div>
      </div>
    `;
    }
}
A2kSelect.styles = css `
    * {
      box-sizing: border-box;
    }

    #select-wrapper {
      align-items: center;
    }

    label {
      min-width: var(--select-label-min-width);
      width: var(--select-label-width);
    }

    #select {
      flex: 1;
      box-shadow: var(--select-border);
    }

    .combo-input {
      height: var(--select-height);
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      background: var(--select-background-color);
      border-top: var(--select-border-top);
      border-left: var(--select-border-left);
      border-bottom: var(--select-border-bottom);
      border-right: var(--select-border-right);
      cursor: pointer;
    }

    .combo-input:focus {
      background: var(--select-option-color-hover);
      color: white;
    }

    .combo-input p {
      padding: var(--select-padding-inline);
    }

    .listbox {
      border: 1px solid black;
      background: white;
      position: absolute;
      min-width: var(--select-min-width);
      width: fit-content;
    }

    slot:not([name])::slotted(*) {
      height: var(--select-height);
      padding-inline: var(--select-padding-inline);
      display: flex;
      align-items: center;
      cursor: var(--cursor-pointer);
    }

    slot:not([name])::slotted(*:hover),
    slot:not([name])::slotted(*:focus) {
      background: var(--select-option-color-hover);
      color: white;
      outline: 1px dotted white;
      outline-offset: -1px;
    }

    a2k-icon {
      font-size: 14px;
      transform: rotate(90deg);
    }
  `;
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], A2kSelect.prototype, "label", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], A2kSelect.prototype, "selectedItem", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], A2kSelect.prototype, "name", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], A2kSelect.prototype, "expanded", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], A2kSelect.prototype, "placeholder", void 0);
